import { useNavigate } from "react-router-dom";
import { useCommon } from "../context/CommonContext";
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../api/Api";
import vasConnectInstance from "./vasconnectInstance";
import Loading from "./Loading";
import { useLanguage } from "../context/LanguageProvider";

const PlayingAroma = () => {
  const navigate = useNavigate();

  // Extract commonly shared data from global context:
  // - memories: list of sample IDs/memories for the experiment
  // - currentMemoryIndex: mutable ref index for the current sample
  // - sampleMode: either "VAS" or some non-device mode
  // - consumptionText: ref to instruction text for sample consumption
  // - vasStatus: ref indicating current VAS connection type (e.g., "websocket")
  // - deviceCount: how many devices are detected by the system
  const { memories, currentMemoryIndex, sampleMode, consumptionText, vasStatus, deviceCount } = useCommon();

  // Grab translation function from language provider for dynamic multi‑language text
  const {translation} = useLanguage();

  // Local UI and control states
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [status, setStatus] = useState("");
  const [canContinue, setCanContinue] = useState(false); // Flag to enable SpaceBar navigation
  const[sampleName, setSampleName] = useState(" ");

 // Persistent mutable refs (won’t trigger re‑render on change)
  const flowRatesRef = useRef(Array(30).fill(0));
  const isProcessingRef = useRef(false); // Prevents duplicate calls
  const vc = vasConnectInstance;

  // Times (in ms) before allowing participant to continue
  const continueTimeNonVAS = 20000;
  const continueTimeVAS = 10000;

  // const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    /**
   * checkStabilization
   * ------------------
   * Polls the VAS device repeatedly until its flow rates match the desired target values
   * within a tight tolerance, for several consecutive checks.
   * 
   * @param deviceID        Hardware device identifier
   * @param targetFlow      Target per-channel flow rates to match
   * @param totalAttempts   # of attempts (used to skip check for first aroma reloads)
   * @param stabilizationTime Delay between stabilization checks (ms)
   * 
   * Returns true if stabilized, false otherwise.
   */
  const checkStabilization = useCallback(async (deviceID, targetFlow, totalAttempts, stabilizationTime) => {
    setStatus("Preparing aroma, Please wait.");
    console.log("inside checkStabilization", { targetFlow });

    // Shortcut: skip check for very first attempt of purge
    if(totalAttempts < 2){
      await new Promise((resolve) => setTimeout(resolve, 1200));
      return true;
    }

    let consecutiveStabilizations = 0;
    const startTime = Date.now();
    const fiveSeconds = 5000;

    while (consecutiveStabilizations < 3) {
      try {
        const { flows: deviceFlows } = await vc.getDeviceDetails(deviceID);
        console.log("Actual:", deviceFlows);

        // Check if each channel's current flow is within +/- 2 units of target
        const stabilized = deviceFlows.every(
          (flow, i) => Math.abs(targetFlow[i] - flow) <= 2
        );

        // const percentageThreshold = 0.05; // 5% expressed as a decimal

        // const stabilized = deviceFlows.every((flow, i) => {
        //   const target = targetFlow[i];
        //   if(target === 0){
        //     return flow >= -0.50 && flow<= 0.50;
        //   }else{
        //     const lowerBound = target * (1 - percentageThreshold);
        //     const upperBound = target * (1 + percentageThreshold);
        //     return flow >= lowerBound && flow <= upperBound;
        //   }
        // });

        if (stabilized) {
          consecutiveStabilizations++;
          console.log(`Stabilization check: Stabilized (${consecutiveStabilizations}/3)`);
        } else {
          // Reset consecutive streak if an out‑of‑tolerance reading occurs
          consecutiveStabilizations = 0;
          console.log(`Stabilization check: Not stabilized`);
        }

        // Warn user if >5s have passed and we still haven't stabilized
        if (Date.now() - startTime >= fiveSeconds && consecutiveStabilizations < 3) {
          setErrorMessage(translation("samples_VASNotStabilized"));
        }

        // If stabilized 3x in a row, clear errors and return success
        if (consecutiveStabilizations >= 3) {
          setErrorMessage("");
          return true;
        }

        // Wait before next check
        await new Promise((resolve) => setTimeout(resolve, stabilizationTime));
      } catch (error) {
        console.error("Stabilization check failed:", error);
        setErrorMessage(translation("samples_ErrorCheckingStabilization"));
        return false;
      }
    }

    return true; // This line should never be reached due to the while loop
  }, []);

  /**
     * playMemory
     * ----------
     * Sends flow rates to the VAS device to play the aroma sample, waits for 
     * stabilization, and then enables "continue" after a short delay.
   */
  const playMemory = useCallback(async (device) => {
    console.log("inside playMemory");
    console.log(`isprocessingref value - ${isProcessingRef.current}`);
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    setStatus("Preparing aroma, Please wait.");

    try {
      await vc.updateDevice(device.id, {
        setpoints: flowRatesRef.current,
        fanSpeed: 6,
      });
    
      const totalAttempts = 6;
      let stabilizationTime = 800;
      const isStable = await checkStabilization(device.id, flowRatesRef.current, totalAttempts, stabilizationTime);
      console.log(isStable ? "Device stabilized" : "Device not stabilized");

      if (isStable) {
        setStatus("");
        setLoadingMessage(consumptionText.current);
        setTimeout(() => setCanContinue(true), continueTimeVAS); // Allow navigation after 10s
      } else {
        await vc.updateDevice(device.id, {
          setpoints: Array(30).fill(0),
          fanSpeed: 6,
        });
        setErrorMessage(translation("samples_VASNotStabilized"));
        setStatus("");
      }
    } catch (error) {
      console.error("playMemory error:", error);
      setErrorMessage(translation("samples_ErrorPlayingMemory"));
      setStatus("");
    } finally {
      isProcessingRef.current = false;
    }
  }, [checkStabilization]);

  /**
     * purge
     * -----
     * Prepares the device channels by flushing with 0-flow & high fan speed,
     * checks stabilization, then runs playMemory to load the actual aroma.
   */
  const purge = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    setStatus("Preparing aroma, Please wait.");
    console.log("inside purge");

    try {
      const devices = await vc.listDevices();

      if (devices.length === 0) {
        throw new Error("No devices connected.");
      }
      if (devices.length > 1) {
        throw new Error("Multiple devices connected.");
      }

      const device = devices[0];
      const zeroSetpoints = Array(30).fill(0);

      await vc.updateDevice(device.id, {
        setpoints: zeroSetpoints,
        fanSpeed: 100,
      });

      const totalAttempts = 0;
      let stabilizationTime = 0;
      const isStable = await checkStabilization(device.id, zeroSetpoints, totalAttempts, stabilizationTime);
      console.log(isStable ? "Purge stabilized" : "Purge not stabilized");

      if (isStable) {
        console.log(" inside Purge stabilized");
        isProcessingRef.current = false;
        await playMemory(device);
      } else {
        await vc.updateDevice(device.id, {
          setpoints: zeroSetpoints,
          fanSpeed: 6,
        });
        setErrorMessage(translation("samples_VASNotStabilized"));
        setStatus("");
      }
    } catch (error) {
      console.error("Purge error:", error);
      setErrorMessage(translation("samples_ErrorInVAS"));
      setStatus("");
    } finally {
      isProcessingRef.current = false;
    }
  }, [checkStabilization, playMemory]);

    /**
     * getVASSampleData
     * ----------------
     * Pulls sample flow-rate data for the current memory from the API,
     * stores it in flowRatesRef, clears any errors, and calls purge/playMemory.
   */
  const getVASSampleData = useCallback(async () => {
    if (isProcessingRef.current) return;
    setStatus("Preparing aroma, Please wait.");

    try {
      const data = await api.fetchSample(memories[currentMemoryIndex.current]);
      const newFlowRates = Array(30).fill(0);

      data.forEach((item) => {
        const index = item.channel_id - 1;
        if (index >= 0 && index < newFlowRates.length) {
          newFlowRates[index] = item.flow_rate;
        }
      });

      flowRatesRef.current = newFlowRates;
      setErrorMessage("");
      await purge();
    } catch (error) {
      console.error("Error fetching sample:", error);
      setErrorMessage(translation("samples_FailedToFetchSampleData"));
      setStatus("");
      isProcessingRef.current = false;         // Reset processing flag on fail
      setTimeout(getVASSampleData, 5000);      // Retry after 5s (simple backoff)
    }
  }, [currentMemoryIndex, memories, purge]);

  /**
   * getNonVASSampleData
   * -------------------
   * Simple API fetch that only retrieves the sample's display name,
   * for when running in non-device mode.
   */
  const getNonVASSampleData = async (e) =>{
    try {
      const data = await api.fetchSample(memories[currentMemoryIndex.current]);
      setSampleName(data[0].sample_name);
      }catch{
        console.error(e);
      }
  }

  // Trigger sample data fetch for VAS mode
  useEffect(() => {
    if (sampleMode === "VAS" && vasStatus.current == "websocket" && deviceCount == 1) {
      getVASSampleData();
    }
    else if (sampleMode !== "VAS"){
      getNonVASSampleData();
    }
  }, [sampleMode, getVASSampleData]);

  // Handle keypress for navigation and set canContinue
  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (!canContinue || event.code !== "Space") return;

      if (sampleMode !== "VAS" || (sampleMode === "VAS" && vasStatus.current !== "websocket")) {
        navigate("/IATTestv2");
        return;
      }

      try {
        const devices = await vc.listDevices();
        if (devices[0]) {
          await vc.updateDevice(devices[0].id, {
            setpoints: Array(30).fill(0),
            fanSpeed: 6,
          });
        }
        navigate("/IATTestv2");
      } catch (error) {
        console.error("Navigation error:", error);
        setErrorMessage(translation("samples_ErrorNavigating"));
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    let timer;
    // Set canContinue for non-VAS mode or VAS override mode
    if (!canContinue) {
      if (sampleMode !== "VAS") {
        // Non-VAS mode: set canContinue after 20s
        timer = setTimeout(() => setCanContinue(true), continueTimeNonVAS);
      } else if (sampleMode === "VAS" && (vasStatus.current !== "websocket" || deviceCount !== 1)) {
        // VAS override mode: set canContinue immediately
        setCanContinue(true);
      }
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      clearTimeout(timer);
    };
  }, [canContinue, sampleMode, vasStatus, navigate, continueTimeNonVAS]);


  if (sampleMode === "VAS" && (vasStatus.current !== "websocket" || deviceCount !== 1)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-200">
        {canContinue && (
          <p className="text-4xl text-gray-700 mt-12"> {translation("samples_VASNotConnectedOverride")}</p>
        )}
      </div>
    );
  }

  // Non-VAS mode UI
  if (sampleMode !== "VAS") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 relative">
        <h2 className="text-5xl font-semibold mb-4">
          {consumptionText.current}
        </h2>
        <span className="text-5xl font-semibold mb-4 mt-4">"{sampleName}"</span>
        {canContinue && (
          <p className="absolute bottom-20 text-5xl text-gray-700 mt-12">{translation("samples_PressSpaceToContinue")}</p>
        )}
      </div>
    );
  }

  // VAS mode UI
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 relative">
      {loadingMessage && <div className="text-4xl font-semibold mb-4">{loadingMessage}</div>}
      {errorMessage && <div className="text-3xl font-semibold mb-4 text-red-600">{errorMessage}</div>}
      {status && status == "Preparing aroma, Please wait." ? <Loading/> : <div className="text-6xl font-semibold mb-4">{status}</div>}
      {canContinue && (
        <p className="absolute bottom-20 text-4xl text-gray-700 mt-12">{translation("samples_PressSpaceToContinue")}</p>
      )}
      { /* <h2 className="text-xl font-semibold mt-10 mb-4">
        Testing details (to be removed in final version)
      </h2>
      <h3>
        Sample mode: {sampleMode} | Playing memory: {memories[currentMemoryIndex.current]}
      </h3>
      <h2>Flow Rates:</h2>
      <ul>
        {flowRatesRef.current.map((flowRate, index) =>
          flowRate > 0 ? (
            <li key={index}>
              Channel ID - {index + 1} | Flow Rate - {flowRate}
            </li>
          ) : null
        )}
      </ul> */ }
    </div>
  );
};

export default PlayingAroma;